import { router, usePage } from '@inertiajs/react'
import { Download, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { reportsUsersPath, reportsUsersCsvPath, reportsUsersPdfPath } from '@/shared/lib/routes'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Label } from '@/shared/ui/label'
import { Input } from '@/shared/ui/input'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebUsersReportRow from '@/shared/types/reports/WebUsersReportRow'
import { ReportEmptyState } from '../components/ReportEmptyState'
import { ReportMobileField } from '../components/ReportMobileField'
import { ReportRangeOrderError } from '../components/ReportRangeOrderError'
import { isReportRangeOrderInvalid } from '@/shared/lib/report-range-order'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  clearLocationFilters,
  mergeLocationFilters,
  readLocationFilters,
} from '@/shared/lib/locationFiltersSession'
import { downloadExport } from '@/shared/lib/downloadExport'

interface FiltersShape {
  searchText?: string
  fromDate?: string
  toDate?: string
  siteIds?: number[]
}

interface OrgOption {
  value: number
  label: string
}

interface Props {
  rows: WebUsersReportRow[]
  loggerRows: WebUsersReportRow[]
  meta: PaginationMeta
  filters: FiltersShape
  defaultFromDate: string
  defaultToDate: string
  activeUsersCount: number
  siteOptions: OrgOption[]
  [key: string]: unknown
}

const UsersReportPage = () => {
  const {
    rows,
    loggerRows = [],
    meta,
    filters,
    defaultFromDate,
    defaultToDate,
    activeUsersCount,
    siteOptions,
    currentTenantId,
    currentUserInfo,
  } = usePage<Props & SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  useBreadcrumbs([{ label: t('nav.usersReport') }])

  const [searchText, setSearchText] = useState(filters.searchText ?? '')
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(() =>
    (filters.siteIds ?? []).map((id) => Number(id)),
  )
  const [fromDate, setFromDate] = useState(filters.fromDate ?? defaultFromDate)
  const [toDate, setToDate] = useState(filters.toDate ?? defaultToDate)
  const [isReportLoading, setIsReportLoading] = useState(false)
  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()
  const hydrateRanRef = useRef<string | null>(null)

  useEffect(() => {
    setSearchText(filters.searchText ?? '')
    setSelectedSiteIds((filters.siteIds ?? []).map((id) => Number(id)))
    setFromDate(filters.fromDate ?? defaultFromDate)
    setToDate(filters.toDate ?? defaultToDate)
  }, [
    filters.searchText,
    filters.siteIds,
    filters.fromDate,
    filters.toDate,
    defaultFromDate,
    defaultToDate,
  ])

  // Bespoke hydrate — Site only. Using the generic hook would clobber shared dept/unit dims
  // that this page does not own. Runs once per tenant:user scope.
  useEffect(() => {
    if (currentTenantId == null) return
    const scopeKey = `${currentTenantId}:${currentUserId ?? 'unknown'}`
    if (hydrateRanRef.current === scopeKey) return
    hydrateRanRef.current = scopeKey

    const urlHasSite = (filters.siteIds?.length ?? 0) > 0
    if (urlHasSite) {
      // URL carries a site filter → write it to the shared store so other pages pick it up.
      mergeLocationFilters(currentTenantId, currentUserId, {
        siteIds: (filters.siteIds ?? []).map(Number).filter((id) => id > 0),
      })
      return
    }

    // No URL site → read shared store, prune against available options, apply if non-empty.
    const stored = readLocationFilters(currentTenantId, currentUserId)
    const allowedSiteValues = new Set(siteOptions.map((option) => option.value))
    const prunedSiteIds = stored.siteIds.filter((id) => allowedSiteValues.has(id))
    if (prunedSiteIds.length > 0) {
      setSelectedSiteIds(prunedSiteIds)
      const params: Record<string, string> = { site_ids: prunedSiteIds.join(',') }
      if (fromDate) params.from_date = fromDate
      if (toDate) params.to_date = toDate
      setIsReportLoading(true)
      router.get(reportsUsersPath(), params, {
        preserveState: false,
        onFinish: () => setIsReportLoading(false),
        onCancel: () => setIsReportLoading(false),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, currentUserId])

  const dateRangeInvalid = isReportRangeOrderInvalid(fromDate, toDate)

  const visitUsersReport = (
    path: string,
    query: Record<string, string> = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const visit = beginVisit(JSON.stringify({ path, query }), mode)
    if (!visit) return
    setIsReportLoading(true)
    router.get(path, query, {
      preserveState: visit.keepDropdownOpen,
      ...(visit.keepDropdownOpen ? { preserveScroll: true } : {}),
      onFinish: () => {
        if (visit.isCurrent()) setIsReportLoading(false)
      },
      onCancel: () => {
        if (visit.isCurrent()) setIsReportLoading(false)
      },
    })
  }

  const buildFilterParams = (
    overrides: {
      searchText?: string
      fromDate?: string
      toDate?: string
      siteIds?: number[]
    } = {},
  ) => {
    const st = overrides.searchText ?? searchText
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    const siteIds = overrides.siteIds ?? selectedSiteIds
    const params: Record<string, string> = {}
    const trimmedSearch = st.trim()
    if (trimmedSearch.length > 0) params.search_text = trimmedSearch
    if (siteIds.length > 0) params.site_ids = siteIds.join(',')
    if (fd) params.from_date = fd
    if (td) params.to_date = td
    return params
  }

  const navigate = (
    overrides: {
      searchText?: string
      fromDate?: string
      toDate?: string
      siteIds?: number[]
    } = {},
    mode: FilterVisitMode = 'default',
  ) => {
    const fd = overrides.fromDate ?? fromDate
    const td = overrides.toDate ?? toDate
    if (isReportRangeOrderInvalid(fd, td)) return
    mergeLocationFilters(currentTenantId, currentUserId, {
      siteIds: overrides.siteIds ?? selectedSiteIds,
    })
    visitUsersReport(
      reportsUsersPath(),
      buildFilterParams(overrides),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
  }

  const handleFromDateChange = (val: string) => {
    setFromDate(val)
    navigate({ fromDate: val })
  }

  const handleToDateChange = (val: string) => {
    setToDate(val)
    navigate({ toDate: val })
  }

  const handleSiteIdsCommit = (selected: number[]) => {
    setSelectedSiteIds(selected)
    navigate({ siteIds: selected }, 'commit')
  }

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearchText('')
      navigate({ searchText: '' })
      return
    }
    setSearchText(value)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') navigate({ searchText })
  }

  const handleSearchBlur = () => {
    navigate({ searchText })
  }

  const clearFilters = () => {
    setSearchText('')
    setSelectedSiteIds([])
    setFromDate(defaultFromDate)
    setToDate(defaultToDate)
    clearLocationFilters(currentTenantId, currentUserId)
    visitUsersReport(reportsUsersPath(), {})
  }

  const handlePageChange = (page: number) => {
    if (dateRangeInvalid) return
    visitUsersReport(reportsUsersPath(), { ...buildFilterParams(), page: String(page) })
  }

  const handleExportCsv = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsUsersCsvPath()}${queryString ? `?${queryString}` : ''}`,
      'export.csv',
    )
  }

  const handleExportPdf = () => {
    if (dateRangeInvalid) return
    const params = buildFilterParams()
    const queryString = new URLSearchParams(params).toString()
    void downloadExport(
      `${reportsUsersPdfPath()}${queryString ? `?${queryString}` : ''}`,
      'export.pdf',
    )
  }

  const activeFilterCount = [
    searchText.trim().length > 0,
    selectedSiteIds.length > 0,
    fromDate !== defaultFromDate,
    toDate !== defaultToDate,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start">
      <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-1 lg:min-w-[12rem]">
        <Label className="text-xs text-muted-foreground">{t('reports.users.searchLabel')}</Label>
        <Input
          value={searchText}
          onChange={(event) => handleSearchChange(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={handleSearchBlur}
          placeholder={t('reports.users.searchPlaceholder')}
          className="w-full lg:w-56"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.users.filterSite')}</Label>
        <MultiSelectCombobox
          options={(siteOptions ?? []).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedSiteIds}
          onChange={setSelectedSiteIds}
          onClose={handleSiteIdsCommit}
          placeholder={t('reports.users.filterSitePlaceholder')}
          className="w-full lg:w-auto lg:min-w-40 lg:max-w-64"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.users.fromDate')}</Label>
        <DatePickerInput
          value={fromDate}
          onChange={handleFromDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('reports.users.toDate')}</Label>
        <DatePickerInput
          value={toDate}
          onChange={handleToDateChange}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <ReportRangeOrderError show={dateRangeInvalid} />
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="min-w-0 text-xl font-bold sm:text-2xl">{t('reports.users.title')}</h1>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.users.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPdf}
            disabled={dateRangeInvalid}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {t('reports.users.exportPdf')}
          </Button>

          <div className="lg:hidden">
            <Sheet
              onOpenChange={(open) => {
                sheetOpenRef.current = open
                if (!open) navigate({}, 'sheet-close')
              }}
            >
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  {t('reports.users.filters')}
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent
                side="bottom"
                className="rounded-t-xl px-4 pt-4 pb-8"
                title={t('reports.users.filters')}
                hideCloseButton
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="font-semibold">{t('reports.users.filters')}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        onClick={clearFilters}
                      >
                        {t('reports.users.clearFilters')}
                      </Button>
                    )}
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" aria-label={t('reports.users.filters')}>
                        <X className="h-4 w-4" />
                      </Button>
                    </SheetClose>
                  </div>
                </div>
                <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {t('reports.users.activeUsersTally', { count: activeUsersCount })}
      </p>

      {isReportLoading && rows.length > 0 ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {t('reports.users.loading')}
        </p>
      ) : null}

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('reports.users.clearFilters')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 && loggerRows.length === 0 && !isReportLoading ? (
        <ReportEmptyState message={t('reports.users.noRecords')} />
      ) : rows.length === 0 && loggerRows.length === 0 && isReportLoading ? (
        <div
          className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 px-4 py-16 text-center"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm font-medium text-muted-foreground">{t('reports.users.loading')}</p>
        </div>
      ) : (
        <div className={`space-y-8 ${isReportLoading ? 'opacity-60' : ''}`}>
          <section className="space-y-4">
            <h2 className="text-lg font-semibold">
              {t('reports.users.sectionUsers', { count: meta.total })}
            </h2>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.users.noRecords')}</p>
            ) : (
              <>
                <div className="space-y-2 lg:hidden">
                  {rows.map((row) => (
                    <UserReportMobileCard key={`user-${row.id}`} row={row} />
                  ))}
                </div>

                <div className="hidden lg:block">
                  <UsersReportTable rows={rows} />
                </div>

                <DataPagination
                  meta={meta}
                  onPageChange={handlePageChange}
                  noun={t('reports.users.paginationNoun')}
                />
              </>
            )}
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold">
              {t('reports.users.sectionLoggers', { count: loggerRows.length })}
            </h2>
            {loggerRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.users.noLoggers')}</p>
            ) : (
              <>
                <div className="space-y-2 lg:hidden">
                  {loggerRows.map((row) => (
                    <UserReportMobileCard key={`logger-${row.id}`} row={row} />
                  ))}
                </div>
                <div className="hidden lg:block">
                  <UsersReportTable rows={loggerRows} />
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  )
}

const UserReportMobileCard = ({ row }: { row: WebUsersReportRow }) => (
  <div className="rounded-lg border bg-card p-3 shadow-sm">
    <p className="font-medium text-foreground [overflow-wrap:anywhere]">{row.displayName}</p>
    {row.email ? (
      <p className="mt-0.5 text-sm text-muted-foreground [overflow-wrap:anywhere]">{row.email}</p>
    ) : null}
    <p className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">
      {[row.siteNames, row.departmentNames].filter(Boolean).join(' · ') || '—'}
    </p>
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
      <ReportMobileField label={t('reports.users.colTasksCompleted')}>
        {row.tasksCompleted}
      </ReportMobileField>
    </div>
  </div>
)

const UsersReportTable = ({ rows }: { rows: WebUsersReportRow[] }) => (
  <div className="rounded-md border">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('reports.users.colUser')}</TableHead>
          <TableHead>{t('reports.users.colEmail')}</TableHead>
          <TableHead>{t('reports.users.colSites')}</TableHead>
          <TableHead>{t('reports.users.colDepartments')}</TableHead>
          <TableHead className="text-right">{t('reports.users.colTasksCompleted')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium">{row.displayName}</TableCell>
            <TableCell className="text-muted-foreground">{row.email || '—'}</TableCell>
            <TableCell className="text-muted-foreground">{row.siteNames ?? '—'}</TableCell>
            <TableCell className="text-muted-foreground">{row.departmentNames ?? '—'}</TableCell>
            <TableCell className="text-right">{row.tasksCompleted}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
)

export default UsersReportPage
